import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type AxiosResponse,
} from 'axios'
import {
  type ApiPaginatedResponse,
  type ApiResponse,
  type ApiRouteProp,
  type HttpMethod,
  type PaginatedResponse,
  Roles,
  type SendableObject,
  SupportedApiMethods,
  type WireFor,
} from '@/lib/types'
import { useCookie } from '@/lib/utils/useCookie'
import { useGlobalStore } from '@/stores/globalStore'
import { useNotificationStore } from '@/stores/notificationStore'
import { i18n } from '@/lib/i18n'
import {
  FaroEvents,
  anonymousFlagFor,
  emitError,
  emitEvent,
} from '@/lib/observability/events'
import { XSRF_COOKIE_NAME } from './env'
import { notifyApiMutation } from './apiMutations'

// ---------------------------------------------------------------
// HTTP client. Class-based, module-singleton (see `useAPI` below).
// Cross-cutting decisions (notify? redirect on 401?) live on the
// SendableObject builder returned by `makeApiRoute(...)`.
//
// On 401, the SendableObject's catch block calls
// `globalStore.clearAuth()` — a state mutation, NOT a router
// push. The globalStore watcher catches the null transition and
// handles the lockout (cancel in-flight + navigate to Login).
// Keeps the HTTP layer free of routing concerns.
// ---------------------------------------------------------------

/**
 * Universal retry policy applied to every request the client makes.
 * Failed requests are retried up to `MAX_RETRIES` times when the
 * failure is classified as transient (network error, 5xx, 429, or
 * a CSRF/cookie issue). Backoff is exponential, capped at
 * `RETRY_BACKOFF_MAX_MS`.
 *
 * Capping matters: without bounds, the retry path turned into a
 * client-side request storm under a misconfigured-CORS condition
 * (observed at ~200 RPS sustained against `/sanctum/csrf-cookie`
 * from a single tab — see incident on 2026-05-13).
 *
 * 4xx (other than 429) are NOT retried — they're client-fault
 * conditions where retry can't change the outcome.
 */
const MAX_RETRIES = 3
const RETRY_BACKOFF_BASE_MS = 1_000
const RETRY_BACKOFF_MAX_MS = 30_000

/**
 * Abortable sleep. Resolves after `ms` OR rejects with an
 * `AbortError` if `signal` aborts first. Lets the retry-backoff
 * wait participate in request cancellation — auth lockout (and
 * future route-change cancellation) kills sleeping retries instead
 * of letting them fire after the user moved on.
 */
const sleep = (ms: number, signal?: AbortSignal): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'))
      return
    }
    const timer = window.setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    const onAbort = (): void => {
      window.clearTimeout(timer)
      reject(new DOMException('Aborted', 'AbortError'))
    }
    signal?.addEventListener('abort', onAbort, { once: true })
  })

/**
 * Operating-tenant channel — picks `tenant_id` out of a body
 * payload and lifts it into the `X-Operating-Tenant` header.
 *
 * Why centralized here: super_admin's per-request tenant context
 * needs to travel with the write, and putting it on the wire body
 * as a `tenant_id` field is the most natural FE-side carrier (the
 * TenantSelect picker writes to that field directly; non-super-
 * admin call sites never include it). Lifting + stripping happens
 * once here instead of being plumbed through every composable.
 *
 * **Authorization** — only platform-tier actors (super_admin /
 * system) trigger the lift. Other roles passing a `tenant_id`
 * field (rare, but possible if a future entity legitimately has
 * one) get it forwarded untouched — the BE will validate and
 * reject if it's used for cross-tenant access; legitimate writes
 * pass through unchanged.
 *
 * **Null guard** — `tenant_id: null` (operator hasn't picked yet)
 * is stripped from the body but does NOT attach a header. The
 * call site is responsible for gating Save when a tenant pick
 * is required (super_admin + CREATE form).
 *
 * Returns the cleaned body + the header to merge (or null when
 * no operating-tenant override applies).
 */
const OPERATING_TENANT_HEADER = 'X-Operating-Tenant'

const extractOperatingTenant = (
  body: unknown,
): { body: unknown; header: Record<string, string> | null } => {
  if (!body || typeof body !== 'object' || Array.isArray(body)) {
    return { body, header: null }
  }
  const obj = body as Record<string, unknown>
  if (!('tenant_id' in obj)) {
    return { body, header: null }
  }
  // Gate on the actor's role. globalStore is Pinia-installed by
  // app bootstrap; by the time any HTTP call fires it's available.
  const isSuperAdmin = useGlobalStore().user.hasRoleAnywhere([
    Roles.SUPER_ADMIN,
    Roles.SYSTEM,
  ])
  if (!isSuperAdmin) {
    return { body, header: null }
  }
  const { tenant_id, ...cleaned } = obj
  if (tenant_id === null || tenant_id === undefined) {
    return { body: cleaned, header: null }
  }
  return {
    body: cleaned,
    header: { [OPERATING_TENANT_HEADER]: String(tenant_id) },
  }
}

/**
 * Parse an HTTP `Retry-After` header. Spec allows either a delta
 * in seconds or an HTTP-date. Returns milliseconds, or `null` if
 * the header is absent or unparseable. Honored on 429 (throttled)
 * responses in lieu of computed exponential backoff.
 */
const parseRetryAfter = (header: string | undefined | null): number | null => {
  if (!header) return null
  const trimmed = header.trim()
  if (/^\d+$/.test(trimmed)) {
    const seconds = Number.parseInt(trimmed, 10)
    return Number.isFinite(seconds) ? seconds * 1_000 : null
  }
  const ts = Date.parse(trimmed)
  if (Number.isNaN(ts)) return null
  return Math.max(0, ts - Date.now())
}

class BackendApi {
  private readonly hostUrl: string

  /**
   * Single-flight latch for the CSRF refresh. Concurrent retries
   * triggered by a CSRF issue await the same in-flight refresh
   * instead of each firing their own — protects against the
   * N-parallel-failures → N-parallel-refreshes amplification.
   *
   * Lives on the class (singleton) so it deduplicates across the
   * per-request axios instances built by `createAxios()`.
   */
  private refreshInFlight: Promise<void> | null = null

  /**
   * Registry of every in-flight request's `AbortController`. Used
   * by `cancelAllInFlight()` to terminate the entire active set on
   * auth lockout (or any other reason a caller wants to drop the
   * world). Each `.callApi()` registers on entry, deregisters on
   * settle.
   */
  private readonly inFlight = new Set<AbortController>()

  /**
   * Set when `cancelAllInFlight('auth-lockout')` has fired. While
   * locked-out, any new `.callApi()` short-circuits — prevents the
   * window between "auth state cleared" and "router push to Login
   * completes" from leaking new requests with a stale session.
   * Cleared by `resumeAfterLockout()` (called from the login flow
   * once a fresh user is in hand).
   */
  private lockedOut = false

  constructor() {
    // OnPrem prebuilt bundles ship without VITE_API_URL/VITE_HOST baked in,
    // so one build serves any client HOST. The standalone appliance runs
    // SAME-ORIGIN API (SAME_ORIGIN_API=1: nginx proxies /api, /sanctum and
    // /auth on the FE origin so the browser needs only ONE accepted cert),
    // so the runtime fallback is the page's own origin — never api.${HOST},
    // whose cert the browser has never accepted.
    const envHost =
      (import.meta.env.VITE_API_URL as string | undefined) ??
      (import.meta.env.VITE_HOST ? `api.${import.meta.env.VITE_HOST}` : window.location.origin)
    this.hostUrl = envHost.startsWith('http') ? envHost : `https://${envHost}`
  }

  /**
   * Absolute URL that begins a server-side SSO sign-in. Used as a full
   * browser navigation (`window.location`), NOT an XHR — the OAuth
   * redirect/callback round-trip happens at the top level. `return`
   * carries the SPA origin so the callback can bounce back here (the
   * server validates it against the trusted-origin allowlist).
   */
  public ssoRedirectUrl(provider: string): string {
    const ret = encodeURIComponent(window.location.origin)
    return `${this.hostUrl}/auth/sso/${provider}/redirect?return=${ret}`
  }

  /**
   * Abort every in-flight request. Called by `globalStore`'s
   * user-state watcher when `user.detail` flips to null
   * (logout, 401, session expiry). Optional `reason` is attached
   * to the AbortSignal so consumers / error logs can distinguish
   * "cancelled because auth ended" from "cancelled because the
   * user navigated away".
   */
  public cancelAllInFlight(reason: string = 'cancelled'): void {
    this.lockedOut = reason === 'auth-lockout'
    if (this.lockedOut) {
      emitEvent(FaroEvents.AUTH_LOCKOUT, () => ({ pendingCount: this.inFlight.size }))
    }
    for (const controller of this.inFlight) {
      controller.abort(reason)
    }
    this.inFlight.clear()
  }

  /**
   * Clear the lockout latch — call this from the login flow after
   * a fresh user is in hand so subsequent requests are allowed
   * through again.
   */
  public resumeAfterLockout(): void {
    this.lockedOut = false
  }

  /**
   * Pre-fetch Sanctum's XSRF cookie. Call BEFORE the first stateful
   * write (login, password change, etc.) so the request carries the
   * X-XSRF-TOKEN header axios derives from `document.cookie`.
   *
   * Why not a regular `makeApiRoute(...)` entry: Sanctum publishes
   * `/sanctum/csrf-cookie` at the application root, NOT under the
   * `/api/` prefix the rest of the client uses. The single-flight
   * latch (shared with the interceptor's retry path) means N
   * concurrent callers share ONE refresh.
   */
  public ensureCsrfCookie(): Promise<void> {
    return this.refreshCsrfToken(this.createAxios())
  }

  /**
   * Build a fresh axios instance per request. The instance carries
   * its own interceptor closure but reads its single-flight latch
   * from `this` (the class singleton).
   *
   * Why per-request and NOT a shared axios singleton: a long-lived
   * axios accumulates mutable surface area — `defaults.headers`,
   * `defaults.baseURL`, attached interceptors. Anything written
   * there leaks across every subsequent request (e.g., an
   * `Authorization: Bearer <token>` set at login persisting after
   * logout). Building fresh per call closes that hole structurally
   * — the request can't see state from a prior request because
   * the instance carrying that state is already GC'd.
   *
   * Allocation cost: ~one axios object + one interceptor closure
   * per `.send()`. Negligible at the volumes this client sees.
   */
  private createAxios(): AxiosInstance {
    const instance = axios.create({
      baseURL: `${this.hostUrl}/api`,
      timeout: 10_000,
      withCredentials: true,
      withXSRFToken: true,
      xsrfCookieName: XSRF_COOKIE_NAME,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    })
    this.setupInterceptors(instance)
    return instance
  }

  /**
   * Discriminate between single and paginated wire shapes and hand
   * the caller the right payload.
   *
   * Wire shapes:
   *   - Single:    `{ responseCode, message, data: T }`
   *   - Paginated: `{ responseCode, message, data: T[], meta, links }`
   *
   * Detection is by BODY SHAPE — Laravel doesn't emit a paginated
   * marker header, but `meta` + `links` siblings of an array `data`
   * are unique to `LengthAwarePaginator` going through `JsonResource`
   * (verified by `backend/tests/Feature/Framework/AsJsonPaginatorTest.php`).
   * Three independent indicators must align (array data, meta key,
   * links key) — no realistic false positive for single resources.
   *
   * Paginated branch: construct a `PaginatedResponse<T>` object —
   * envelope siblings (`responseCode` / `message`) are dropped, the
   * three caller-facing fields are forwarded verbatim. Caller reads
   * `resp.data` for items, `resp.meta.total` for total, etc.
   *
   * Single branch: peel the envelope and return `data` directly.
   * Bodies without `data` (rare; mostly delete-with-no-payload) pass
   * through as-is.
   */
  private static unwrapBody<TResp>(resp: WireFor<TResp> | undefined): TResp | undefined {
    if (!resp || typeof resp !== 'object') {
      return resp as TResp | undefined
    }
    if (!('data' in resp)) {
      return resp as TResp | undefined
    }
    const isPaginated =
      Array.isArray((resp as { data?: TResp }).data) &&
      'meta' in resp &&
      'links' in resp
    if (isPaginated) {
      const wire = resp as ApiPaginatedResponse<TResp>
      const constructed: PaginatedResponse<TResp> = {
        data: wire.data,
        meta: wire.meta,
        links: wire.links,
      }
      return constructed as TResp
    }
    return (resp as ApiResponse<TResp>).data
  }

  /**
   * Dispatch by HTTP method. The BE wire envelope is unwrapped here
   * before handing the result to the caller — see `unwrapBody` below
   * for the single-vs-paginated discrimination.
   *
   * Body is typed via `TReq`; response via `TResp`. Use `never`
   * for `TReq` on GET/DELETE.
   */
  public async callApi<TReq, TResp>(
    uri: string,
    method: SupportedApiMethods,
    data?: TReq,
    config?: AxiosRequestConfig,
  ): Promise<TResp | undefined> {
    // Short-circuit during auth lockout. Without this, a request
    // queued in the microtask gap between "401 surfaced" and
    // "router.push to Login resolved" would fire with a stale
    // session cookie and land in a UI that's about to unmount.
    if (this.lockedOut) {
      return Promise.reject({
        status: 401,
        statusText: 'Locked Out',
        data: { message: 'Session ended; awaiting re-authentication.' },
        headers: {},
        config: {},
      } as AxiosResponse)
    }

    // Per-request AbortController. If the caller passed their own
    // `signal` (e.g., a route-scoped one), we chain — aborting
    // either source aborts our internal controller. Registered in
    // `inFlight` so `cancelAllInFlight()` can sweep the whole set.
    //
    // axios types `signal` as its own `GenericAbortSignal` (narrower
    // than DOM `AbortSignal` — no `reason`, optional listener methods).
    // We only ever pass DOM `AbortSignal`s from this codebase, so the
    // cast is safe; runtime-guard the listener call anyway in case a
    // future caller hands in something exotic.
    const controller = new AbortController()
    const callerSignal = config?.signal as AbortSignal | undefined
    if (callerSignal) {
      if (callerSignal.aborted) {
        controller.abort(callerSignal.reason)
      } else if (typeof callerSignal.addEventListener === 'function') {
        callerSignal.addEventListener('abort', () => controller.abort(callerSignal.reason), {
          once: true,
        })
      }
    }
    this.inFlight.add(controller)

    let effectiveConfig: AxiosRequestConfig = { ...(config ?? {}), signal: controller.signal }

    // Auto-lift the operating tenant from body → header. POST/PATCH/
    // PUT carry their body in `data`; DELETE-with-body carries it
    // in `config.data` (axios delete signature has no positional
    // body arg). Inspect both. See {@see extractOperatingTenant}.
    let effectiveData: TReq | undefined = data
    if (method === SupportedApiMethods.DELETE) {
      const { body: cleaned, header } = extractOperatingTenant(effectiveConfig.data)
      if (cleaned !== effectiveConfig.data) {
        effectiveConfig = { ...effectiveConfig, data: cleaned }
      }
      if (header) {
        effectiveConfig = {
          ...effectiveConfig,
          headers: { ...(effectiveConfig.headers ?? {}), ...header },
        }
      }
    } else if (data !== undefined) {
      const { body: cleaned, header } = extractOperatingTenant(data)
      effectiveData = cleaned as TReq | undefined
      if (header) {
        effectiveConfig = {
          ...effectiveConfig,
          headers: { ...(effectiveConfig.headers ?? {}), ...header },
        }
      }
    }

    const instance = this.createAxios()
    try {
      // Wire shape is predicted by `WireFor<TResp>` — paginated wire
      // when caller's TResp is `PaginatedResponse<X>`, single wire
      // otherwise. `unwrapBody` confirms at runtime and narrows.
      let resp: WireFor<TResp> | undefined
      switch (method) {
        case SupportedApiMethods.GET:
          resp = (await instance.get<WireFor<TResp>>(uri, effectiveConfig)).data
          break
        case SupportedApiMethods.DELETE:
          resp = (await instance.delete<WireFor<TResp>>(uri, effectiveConfig)).data
          break
        case SupportedApiMethods.POST:
          resp = (await instance.post<WireFor<TResp>>(uri, effectiveData, effectiveConfig)).data
          break
        case SupportedApiMethods.PATCH:
          resp = (await instance.patch<WireFor<TResp>>(uri, effectiveData, effectiveConfig)).data
          break
        case SupportedApiMethods.PUT:
          resp = (await instance.put<WireFor<TResp>>(uri, effectiveData, effectiveConfig)).data
          break
        default:
          return Promise.reject({ responseCode: 405, message: 'Unsupported API method' })
      }

      const body = BackendApi.unwrapBody<TResp>(resp)
      if (method !== SupportedApiMethods.GET) notifyApiMutation()
      return body
    } catch (error) {
      if (!axios.isAxiosError(error)) {
        // Non-axios throw — usually programmer error or an aborted
        // signal that bubbled up. Push the original Error so Faro
        // captures the stack frame. Closure form so the attr dict
        // isn't built when observability is off.
        emitError(error, () => ({
          uri,
          method,
          phase: 'callApi.non_axios',
        }))
        return Promise.reject({
          status: 500,
          statusText: 'Internal Error',
          data: { message: error instanceof Error ? error.message : 'unknown error' },
          headers: {},
          config: {},
        } as AxiosResponse)
      }
      // Axios error reached us — retries already exhausted in the
      // interceptor OR the failure class was non-retryable. Push
      // the AxiosError so Faro captures stack + axios's enriched
      // context (config.url, response.status, etc.).
      const isNetworkError = error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED'
      emitError(error, () => ({
        uri,
        method,
        status: error.response?.status !== undefined ? String(error.response.status) : 'none',
        code: error.code ?? 'none',
        anonymous: anonymousFlagFor(useGlobalStore().user.detail),
        phase: isNetworkError ? 'callApi.network' : 'callApi.http_error',
      }))
      if (isNetworkError) {
        emitEvent(FaroEvents.HTTP_REQUEST_NETWORK_ERROR, () => ({
          uri,
          method,
          code: error.code ?? 'unknown',
        }))
      }
      return Promise.reject(
        error.response ??
          ({
            status: 400,
            statusText: error.name,
            data: { message: error.message },
            headers: error.request?.headers ?? {},
            config: {},
          } as AxiosResponse),
      )
    } finally {
      // Deregister whether the call resolved, rejected, or was
      // aborted. Keeps `inFlight` tight so a future
      // `cancelAllInFlight()` only touches actually-pending work.
      this.inFlight.delete(controller)
    }
  }

  /**
   * Universal retry interceptor. Replays a failed request when
   * the failure is classified as transient, up to `MAX_RETRIES`
   * times with exponential backoff capped at
   * `RETRY_BACKOFF_MAX_MS`.
   *
   * Retryable conditions (any one triggers a retry):
   *   - CSRF/cookie issue (`ERR_NETWORK` with missing XSRF cookie,
   *     or a `CSRF token mismatch` payload). The retry path
   *     additionally awaits a CSRF refresh before replaying.
   *   - Network-level failure (`ERR_NETWORK`, `ECONNABORTED`).
   *   - Server-side error (HTTP 5xx).
   *   - Throttled (HTTP 429).
   *
   * NOT retried:
   *   - Other 4xx — client-fault conditions where retry can't
   *     change the outcome (validation error, auth failure,
   *     forbidden, not found).
   *   - The CSRF refresh endpoint itself — it IS the recovery
   *     path; retrying it recursively was the loop bug fixed on
   *     2026-05-13.
   *
   * Per-request `_retryCount` lives on the AxiosRequestConfig so
   * each request has its own budget. Single-flight latch on the
   * CSRF refresh (`refreshCsrfToken`) ensures N concurrent
   * failing requests share ONE refresh, not N refreshes.
   */
  private setupInterceptors(instance: AxiosInstance): void {
    instance.interceptors.response.use(
      (r) => r,
      async (
        error: AxiosError & {
          config: AxiosRequestConfig & { _retryCount?: number }
        },
      ) => {
        const originalRequest = error.config
        if (!originalRequest) return Promise.reject(error)

        // The CSRF refresh endpoint is the recovery path; never
        // retry it from inside the interceptor or the path loops.
        const url = originalRequest.url ?? ''
        if (url.includes('sanctum/csrf-cookie')) {
          return Promise.reject(error)
        }

        const status = error.response?.status

        // CSRF-issue detection — DRIVEN BY STATUS CODE 419, not by body
        // message. Laravel returns 419 for CSRF token mismatch in BOTH
        // forms: JSON (when Accept: application/json was negotiated) AND
        // HTML "Page Expired" (when no JSON accept header was matched).
        // Matching the JSON-body `.message` string would miss the HTML
        // case — that bug let CSRF retries silently skip the refresh
        // path in fresh-install / stale-cookie scenarios.
        //
        // Network-error fallback retained for the case where the request
        // itself errored before reaching the server (e.g. cookie sweep
        // happened mid-flight; ERR_NETWORK + missing XSRF cookie =
        // almost certainly a CSRF state issue).
        const isCsrfIssue =
          status === 419 ||
          (error.code === 'ERR_NETWORK' && !useCookie().isCookieValid(XSRF_COOKIE_NAME))
        const isNetworkError = error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED'
        const isServerError = status !== undefined && status >= 500 && status < 600
        const isThrottled = status === 429

        const isRetryable = isCsrfIssue || isNetworkError || isServerError || isThrottled
        if (!isRetryable) {
          // Non-retryable failure — auth-class (401/403) lands here,
          // as does validation (422) etc. Surface the 401 as a
          // distinct event so dashboards can chart "unauth rate"
          // without parsing every error log.
          if (status === 401) {
            emitEvent(FaroEvents.AUTH_UNAUTHORIZED, () => ({ uri: url }))
          }
          return Promise.reject(error)
        }

        const attempt = originalRequest._retryCount ?? 0
        if (attempt >= MAX_RETRIES) return Promise.reject(error)
        originalRequest._retryCount = attempt + 1

        emitEvent(FaroEvents.HTTP_REQUEST_RETRY, () => ({
          uri: url,
          attempt: attempt + 1,
          reason: isCsrfIssue
            ? 'csrf'
            : isThrottled
              ? 'throttled'
              : isServerError
                ? 'server_error'
                : 'network',
          status: status !== undefined ? String(status) : 'none',
        }))

        // Backoff source priority:
        //   1. Server-supplied `Retry-After` on 429 (spec-compliant
        //      throttling — server tells us when to come back).
        //   2. Exponential `min(base * 2^attempt, max)` otherwise.
        // Either way, capped at RETRY_BACKOFF_MAX_MS so a malformed
        // or hostile header can't stall the client for hours.
        const retryAfterHeader = (error.response?.headers as Record<string, string> | undefined)?.[
          'retry-after'
        ]
        const serverBackoff = isThrottled ? parseRetryAfter(retryAfterHeader ?? null) : null
        const backoffMs = Math.min(
          serverBackoff ?? RETRY_BACKOFF_BASE_MS * 2 ** attempt,
          RETRY_BACKOFF_MAX_MS,
        )
        // Pass the request's signal so a mid-backoff
        // `cancelAllInFlight()` actually wakes the sleeper.
        await sleep(backoffMs, originalRequest.signal as AbortSignal | undefined)

        // CSRF refresh is awaited inline so the replay carries a
        // fresh token. Other retry classes just replay.
        if (isCsrfIssue) {
          // First retry attempt for 419: simple CSRF refresh (covers
          // the common "token rotated mid-session" case).
          // Second retry attempt for 419 (i.e. the first refresh
          // didn't help): the cookies in the browser are stale —
          // most likely a backend redeploy with a new APP_KEY left
          // the browser holding a session cookie that no longer
          // decrypts on the server. Wipe the auth cookies + fetch
          // a fresh CSRF cookie. After this, the replay should
          // succeed against a clean slate.
          if (attempt === 0) {
            emitEvent(FaroEvents.HTTP_CSRF_REFRESH, () => ({ uri: url }))
            try {
              await this.refreshCsrfToken(instance)
            } catch (refreshErr) {
              emitEvent(FaroEvents.HTTP_CSRF_REFRESH_FAILED, () => ({ uri: url }))
              emitError(refreshErr, () => ({
                uri: url,
                phase: 'interceptor.csrf_refresh',
              }))
              throw refreshErr
            }
          } else {
            // Recovery path — clear stale auth cookies, get a fresh
            // CSRF cookie, replay.
            emitEvent(FaroEvents.HTTP_CSRF_REFRESH, () => ({
              uri: url,
              mode: 'stale_cookies_clear',
            }))
            this.wipeAuthCookies()
            try {
              await this.refreshCsrfToken(instance)
            } catch (refreshErr) {
              emitEvent(FaroEvents.HTTP_CSRF_REFRESH_FAILED, () => ({
                uri: url,
                mode: 'stale_cookies_clear',
              }))
              emitError(refreshErr, () => ({
                uri: url,
                phase: 'interceptor.csrf_refresh.stale_clear',
              }))
              throw refreshErr
            }
          }
        }

        return instance(originalRequest)
      },
    )
  }

  /**
   * Wipe the XSRF cookie ahead of the stale-cookies recovery refresh
   * (second 419 in a row after a clean CSRF refresh), so the replay
   * can't pick up the value that just failed.
   *
   * Scope is deliberately just the XSRF cookie. The session cookie is
   * HttpOnly and lives on the API host, so `document.cookie` can
   * neither read nor delete it — clearing that one is the server's
   * job, and the API does it on the 419 itself
   * (`ClearUnusableAuthCookies`). An earlier version of this method
   * listed session-cookie names here; those deletes were silent
   * no-ops in every deployed environment.
   *
   * Sweeps both the domain-scoped variant (Sanctum sets the cookie
   * with `domain=.${HOST}`) and the host-only one.
   */
  private wipeAuthCookies(): void {
    const cookie = useCookie()
    const cookieDomain = `.${(import.meta.env.VITE_HOST as string | undefined) ?? window.location.hostname}`
    cookie.deleteCookie(XSRF_COOKIE_NAME, '/', cookieDomain)
    cookie.deleteCookie(XSRF_COOKIE_NAME, '/')
  }

  /**
   * Single-flight CSRF refresh — concurrent failing requests await
   * the SAME in-flight promise instead of each firing their own.
   * The latch clears on settle (success or failure); the next
   * failure that arrives after settle starts a fresh refresh.
   *
   * Reuses the caller's per-request axios instance to issue the
   * refresh call. The cookie ends up in `document.cookie` either
   * way; the choice of instance is incidental.
   */
  private refreshCsrfToken = (instance: AxiosInstance): Promise<void> => {
    this.refreshInFlight ??= instance
      .get('sanctum/csrf-cookie', { baseURL: this.hostUrl })
      .then(() => undefined)
      .catch(() => undefined)
      .finally(() => {
        this.refreshInFlight = null
      })
    return this.refreshInFlight
  }
}

/**
 * Module-singleton accessor. Previously `useAPI()` returned a fresh
 * `BackendApi` (and a fresh axios instance + interceptor) on every
 * `.send()`. Allocates per-call, defeats the single-flight refresh
 * latch (each instance has its own), and burns interceptor closures
 * on the GC. Singleton is strictly better — the class is stateless
 * across calls except for the refresh latch, which is exactly what
 * MUST be shared.
 */
let apiSingleton: BackendApi | null = null
export const useAPI = (): BackendApi => (apiSingleton ??= new BackendApi())

// ---------------------------------------------------------------
// `makeApiRoute` — fluent route factory. Routes file (`useApiRoutes.ts`)
// uses this to declare endpoints; consumers call
// `API.RESOURCE.<verb>(...).<config>().send(...)`.
//
// URI templating is DUAL-MODE:
//   - If the URI string contains `{?}` markers → template-replace.
//     Example: `makeApiRoute('reset-password?token={?}&id={?}')`.
//   - Otherwise → trailing-path append. Example:
//     `makeApiRoute('users')` then `.get('5', 'edit')` → `users/5/edit`.
//
// Resource-id mutations (patch/put/delete) take `resourceId` FIRST
// and append it as `<base>/<id>`.
// ---------------------------------------------------------------

const HTTP_METHOD_BY_VERB: Record<HttpMethod, SupportedApiMethods> = {
  get: SupportedApiMethods.GET,
  post: SupportedApiMethods.POST,
  patch: SupportedApiMethods.PATCH,
  put: SupportedApiMethods.PUT,
  delete: SupportedApiMethods.DELETE,
}

// Stores are imported eagerly at the top of the file now that the
// `buildUser` extraction broke the prior circular cycle. The store
// FUNCTIONS still resolve lazily at first call site — Pinia
// requires the app to have installed `createPinia()` before
// `useXxxStore()` returns a real store. By the time any
// SendableObject hits its catch block, the app is mounted, so this
// is safe.

export function makeApiRoute(uri: string): ApiRouteProp {
  function resolveUri(args: string[] = [], resourceId?: string | number): string {
    const argsCopy = [...args]
    const hasTemplate = uri.includes('{?}')
    let endpoint = hasTemplate
      ? uri.replace(/\{\?\}/g, () => argsCopy.shift() ?? '').replace(/\/$/, '')
      : uri + argsCopy.reduce((acc, p) => `${acc}/${p}`, '')
    if (resourceId !== undefined && resourceId !== null && `${resourceId}`.length > 0) {
      endpoint = `${endpoint}/${resourceId}`
    }
    return endpoint
  }

  /**
   * Serialize a plain object to a `FormData` instance for
   * `multipart/form-data` uploads. Handles three value shapes:
   *
   *   - `File` / `Blob` → appended as-is (preserves the binary
   *     stream + filename for File)
   *   - Array → appended as repeated fields with `[]` suffix per
   *     multipart convention (`files` → `files[]`)
   *   - Primitives (`string` / `number` / `boolean`) → coerced to
   *     string and appended
   *
   * `null` / `undefined` entries are SKIPPED (FormData would
   * coerce them to literal `"null"` / `"undefined"` strings,
   * which Laravel then sees as the string — surprising for
   * callers passing optional fields as nullish).
   */
  function toFormData(obj: Record<string, unknown>): FormData {
    const fd = new FormData()
    for (const [key, value] of Object.entries(obj)) {
      if (value === null || value === undefined) continue
      if (Array.isArray(value)) {
        for (const v of value) {
          if (v === null || v === undefined) continue
          if (v instanceof File || v instanceof Blob) {
            fd.append(`${key}[]`, v)
          } else {
            fd.append(`${key}[]`, String(v))
          }
        }
        continue
      }
      if (value instanceof File || value instanceof Blob) {
        fd.append(key, value)
      } else {
        fd.append(key, String(value))
      }
    }
    return fd
  }

  function buildSendable<TReq, TResp>(
    verb: HttpMethod,
    args: string[] = [],
    resourceId?: string | number,
  ): SendableObject<TReq, TResp> {
    let notifyOnError = false
    let redirectOnAuthFailed = true
    let perCallTimeout: number | undefined
    let extraHeaders: Record<string, string> | undefined
    // Accumulated query params (`.params({...})` calls merge into this).
    // Stripped of `undefined` / `null` / `''` at request time so callers
    // can treat optional filters as "absent when nullish."
    let accParams: Record<string, unknown> | undefined
    // Accumulated request body (`.body(x)` calls REPLACE — last write
    // wins). Use this OR the positional first arg of `send(data, ...)` —
    // not both. When both supplied, `.body(...)` wins.
    let accBody: TReq | undefined
    // `.multipart()` flag — when set, the body is serialized to
    // FormData (preserves File/Blob streams) and Content-Type
    // switches to multipart/form-data (axios appends the boundary).
    let multipartMode = false
    // Per-request upload progress callback (axios `onUploadProgress`).
    let uploadProgressCb: ((percent: number) => void) | undefined

    const sendable: SendableObject<TReq, TResp> = {
      url: () => resolveUri(args, resourceId),
      notifyOnError(v: boolean) {
        notifyOnError = v
        return sendable
      },
      redirectOnAuthFailed(v: boolean) {
        redirectOnAuthFailed = v
        return sendable
      },
      timeout(ms: number) {
        perCallTimeout = ms
        return sendable
      },
      headers(extra: Record<string, string>) {
        extraHeaders = { ...(extraHeaders ?? {}), ...extra }
        return sendable
      },
      operatingAs(tenantId: number | null | undefined) {
        // No-op when no tenant pick OR the caller isn't platform-tier.
        // `TenantResolver` (BE) would 403 the operating-tenant header for
        // non-super-admin actors anyway; checking here keeps the call
        // site free of role-aware boilerplate (any FE component can
        // chain `.operatingAs(...)` without first checking the role).
        if (tenantId === null || tenantId === undefined) {
          return sendable
        }
        const isSuperAdmin = useGlobalStore().user.hasRoleAnywhere([
          Roles.SUPER_ADMIN,
          Roles.SYSTEM,
        ])
        if (!isSuperAdmin) {
          return sendable
        }
        extraHeaders = {
          ...(extraHeaders ?? {}),
          'X-Operating-Tenant': String(tenantId),
        }
        return sendable
      },
      params(p: Record<string, unknown>) {
        // Merge into accumulated query params. Multiple `.params()`
        // calls accumulate; later keys override earlier on collision.
        // Replaces the older `send(undefined, { params })` positional
        // pattern with a named-arg style that fits the rest of the
        // builder chain.
        accParams = { ...(accParams ?? {}), ...p }
        return sendable
      },
      body(data: TReq) {
        // Set the request body (POST / PATCH / PUT / DELETE-with-body).
        // Last write wins. Replaces the older `send(payload)` positional
        // pattern. The positional first arg of `send()` still works for
        // backward compat — if both supplied, `.body()` takes precedence.
        accBody = data
        return sendable
      },
      multipart() {
        // Switch request encoding to multipart/form-data. The body
        // set via `.body(...)` MUST be a plain object whose values
        // are primitives, File, Blob, or arrays thereof. The
        // send-path serializes to FormData; axios handles the
        // boundary header.
        multipartMode = true
        return sendable
      },
      onUploadProgress(cb: (percent: number) => void) {
        // Axios fires `onUploadProgress` with `{loaded, total}` as
        // bytes flush. Round to integer % for cleaner UI binding;
        // missing `total` (chunked transfer) → fire 0 so callers
        // know something started.
        uploadProgressCb = cb
        return sendable
      },
      send: async (data?: TReq, config?: AxiosRequestConfig): Promise<TResp | undefined> => {
        // Builder state > positional args. `.body()` wins over the
        // `data` first arg; `.params()` merges over `config.params`.
        const effectiveBody: TReq | undefined = accBody ?? data
        const mergedParams: Record<string, unknown> | undefined =
          accParams || config?.params
            ? {
                ...(config?.params as Record<string, unknown> | undefined),
                ...(accParams
                  ? Object.fromEntries(
                      Object.entries(accParams).filter(
                        ([, v]) => v !== undefined && v !== null && v !== '',
                      ),
                    )
                  : {}),
              }
            : undefined
        // DELETE-with-body case: axios's `delete(uri, config)` takes
        // the body via `config.data` (no positional body arg). When
        // the caller used `.body(...)` for DELETE, mirror that into
        // `config.data` so callApi's axios.delete picks it up.
        //
        // GET is NOT in this branch — browsers strip the body on GET
        // (WHATWG XHR / Fetch specs both block it). List / search
        // endpoints use URL query params via `.params(...)` instead.
        const isDelete = HTTP_METHOD_BY_VERB[verb] === SupportedApiMethods.DELETE
        // Multipart: serialize the effective body to FormData. Done
        // here (not in the `.body()` setter) so the body remains
        // typed for chaining; only the wire serialization changes.
        // Axios sets the boundary in the Content-Type header
        // automatically when given a FormData instance.
        let wireBody: unknown = effectiveBody
        let multipartHeader: Record<string, string> | undefined
        if (multipartMode && effectiveBody !== undefined && effectiveBody !== null) {
          wireBody = toFormData(effectiveBody as Record<string, unknown>)
          multipartHeader = { 'Content-Type': 'multipart/form-data' }
        }
        const configDataFromBody =
          isDelete && wireBody !== undefined ? { data: wireBody } : {}
        const mergedHeaders =
          extraHeaders || multipartHeader
            ? { ...(config?.headers ?? {}), ...(extraHeaders ?? {}), ...(multipartHeader ?? {}) }
            : undefined
        const mergedConfig: AxiosRequestConfig = {
          ...(config ?? {}),
          ...configDataFromBody,
          ...(mergedParams ? { params: mergedParams } : {}),
          ...(perCallTimeout !== undefined ? { timeout: perCallTimeout } : {}),
          ...(mergedHeaders ? { headers: mergedHeaders } : {}),
          ...(uploadProgressCb
            ? {
                onUploadProgress: (evt: { loaded: number; total?: number }) => {
                  const pct = evt.total && evt.total > 0
                    ? Math.round((evt.loaded / evt.total) * 100)
                    : 0
                  uploadProgressCb!(pct)
                },
              }
            : {}),
        }
        try {
          return await useAPI().callApi<TReq, TResp>(
            resolveUri(args, resourceId),
            HTTP_METHOD_BY_VERB[verb],
            wireBody as TReq | undefined,
            mergedConfig,
          )
        } catch (error) {
          const errResp = error as AxiosResponse | undefined

          if (notifyOnError) {
            // 419 after the interceptor's retry chain (which includes
            // a fresh CSRF refresh + stale-cookies wipe) means the
            // browser session is genuinely out of sync with the
            // server — the user can't recover by clicking again.
            // Show an actionable message instead of the server's
            // raw "CSRF token mismatch." (meaningless to non-engs).
            //
            // Any other failure: surface the server's message
            // verbatim; that's the contract — backend's error body
            // IS the user-facing message.
            const serverMessage = (errResp?.data as { message?: string } | undefined)?.message
            const friendly =
              errResp?.status === 419
                ? i18n.global.t('app.error.session_expired')
                : (serverMessage ?? i18n.global.t('app.error.requestFailed'))
            useNotificationStore().alertError(friendly)
          }

          // 401 from any request is interpreted as "session ended /
          // never started". Route through `globalStore.clearAuth()` —
          // the single seam for "this user is no longer authed". The
          // store's watcher catches the resulting null transition
          // and handles the lockout: cancelling every OTHER in-flight
          // request and routing to Login. The HTTP layer's only job
          // here is to surface auth-expiration as a state change.
          if (redirectOnAuthFailed && errResp?.status === 401) {
            useGlobalStore().clearAuth()
          }

          throw error
        }
      },
    }
    return sendable
  }

  function getEndpoint(...args: string[]): string {
    return resolveUri(args)
  }

  function getFullUrl(...args: string[]): string {
    // Same fallback rationale as the ApiService constructor: prebuilt OnPrem
    // bundles run same-origin, so fall back to the page's own origin.
    const envHost =
      (import.meta.env.VITE_API_URL as string | undefined) ??
      (import.meta.env.VITE_HOST ? `api.${import.meta.env.VITE_HOST}` : window.location.origin)
    const baseHost = envHost.startsWith('http') ? envHost : `https://${envHost}`
    return `${baseHost}/api/${getEndpoint(...args)}`
  }

  return {
    uri,
    getEndpoint,
    getFullUrl,
    index: <TResp = unknown>(...args: string[]) => buildSendable<never, TResp>('get', args),
    get: <TResp = unknown>(...args: string[]) => buildSendable<never, TResp>('get', args),
    post: <TReq = Record<string, unknown>, TResp = unknown>(...args: string[]) =>
      buildSendable<TReq, TResp>('post', args),
    patch: <TReq = Record<string, unknown>, TResp = unknown>(
      resourceId: string | number,
      ...args: string[]
    ) => buildSendable<TReq, TResp>('patch', args, resourceId),
    put: <TReq = Record<string, unknown>, TResp = unknown>(
      resourceId: string | number,
      ...args: string[]
    ) => buildSendable<TReq, TResp>('put', args, resourceId),
    delete: <TResp = unknown>(resourceId: string | number, ...args: string[]) =>
      buildSendable<never, TResp>('delete', args, resourceId),
  }
}
